import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, forkJoin, map, startWith, switchMap, takeUntil } from 'rxjs';
import countryData from '@assets/data/countries.json';
import naceData from '@assets/data/nace.json';
import { ToastService } from '@services/common/toast.service';
import { UserAction, UserState, UserStatus } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { buildCountryCitiesLUT, City } from 'app/@types/shared/world-cities';
import { UtilsService } from '@services/common/utils.service';
import { Country as CountryType } from 'app/@types/shared/world-cities';
import { NaceCode } from 'app/@types/shared/nace-code';
import { FormulaireCallCompletion } from 'app/@types/shared/formulaire-call-completion';
import { SelectChangeEvent } from 'primeng/select';
import { buildCountryOptions, CountryOption } from 'app/main/helpers/country-options';
import { revealFormErrors } from 'app/main/helpers/form-validation';

interface CognitoIdTokenClaims {
    family_name?: string;
    given_name?: string;
    'custom:role'?: string;
    email?: string;
    phone_number?: string;
}

@Component({
    selector: 'app-settings-form',
    templateUrl: './settings-form.component.html',
    styleUrls: ['./settings-form.component.scss'],
    standalone: false,
})
export class SettingsFormComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims | undefined>;
    @Select(UserState.getUserStatus) userStatus$!: Observable<UserStatus | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$!: Observable<QuestionnaireDto>;
    @Input() isOnboarding: boolean = false;
    @Output() onboardedEvent = new EventEmitter<boolean>();

    public settingsForm!: FormGroup;
    public activities: NaceCode[] = [];
    public selectedIndustryActivity?: NaceCode;
    public firstYears: number[] = [];
    public countries: CountryOption[] = [];
    private countryCitiesLUT: Map<string, City[]> = new Map<string, City[]>();
    public filteredCities: City[] | undefined;

    private userStatus: UserStatus = UserStatus.NONE;
    private ngUnsubscribe = new Subject<void>();
    public userClaim!: UserClaims;
    public questionnaire!: QuestionnaireDto;
    public loading: boolean = false;

    public isAdminForm: boolean = false;
    vatPlaceholder = 'BE0123.456.789';
    constructor(
        private authService: CognitoAuthenticationService,
        private formBuilder: FormBuilder,
        private store: Store,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        private featureService: FeatureService,
        private readonly utilsService: UtilsService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.isAdminForm =
            this.featureService.hasUiFeatures(['organization_admin'], false) || this.featureService.hasSudoFeature();
        this.settingsForm = this.formBuilder.group({
            organizationGroup: this.formBuilder.group({
                companyLogo: new FormControl(''),
                companyName: new FormControl('', Validators.required),
                vatNumber: new FormControl('', CustomValidators.countryVatValidator),
                country: new FormControl('', Validators.required),
                city: new FormControl('0000000'),
                naceCode: new FormControl('', Validators.required),
                industryCode: new FormControl({ value: '', disabled: true }),
                fte: new FormControl(1, [Validators.required, Validators.min(1)]),
                refYear: new FormControl(1, Validators.required),
            }),
            userGroup: this.formBuilder.group({
                lastname: new FormControl('', Validators.required),
                firstname: new FormControl('', Validators.required),
                role: new FormControl(''),
                email: new FormControl({ value: '', disabled: true }, Validators.required),
                phoneNumber: new FormControl(null, CustomValidators.phoneNumberValidator),
            }),
            securityGroup: this.formBuilder.group(
                {
                    oldPassword: new FormControl('', Validators.required),
                    newPassword: new FormControl('', [Validators.required, CustomValidators.passwordValidator]),
                    confirmPassword: new FormControl('', Validators.required),
                },
                {
                    validators: [
                        CustomValidators.mustNotMatch('oldPassword', 'newPassword'),
                        CustomValidators.mustMatch('newPassword', 'confirmPassword'),
                    ],
                },
            ),
        });
    }

    get organizationFormGroup(): FormGroup {
        return this.settingsForm.get('organizationGroup') as FormGroup;
    }

    get userFormGroup(): FormGroup {
        return this.settingsForm.get('userGroup') as FormGroup;
    }

    get securityFormGroup(): FormGroup {
        return this.settingsForm.get('securityGroup') as FormGroup;
    }

    ngOnInit(): void {
        this.userStatus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            this.userStatus = status ?? UserStatus.NONE;
        });

        combineLatest([this.questionnaire$, this.userClaims$, this.translateService.onLangChange.pipe(startWith(null))])
            .pipe(
                takeUntil(this.ngUnsubscribe),
                switchMap(([quest, uc, lang]) => {
                    // Create an array of observables for the translations
                    const translationObservables = naceData.map((item) => {
                        const labelTranslation$ = this.translateService.get(`nace.${item.translationKey}`);
                        const industrySectorLabelTranslation$ = this.translateService.get(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        );

                        // Use forkJoin to combine the two translations for each item
                        return forkJoin([labelTranslation$, industrySectorLabelTranslation$]).pipe(
                            map(([labelTranslation, industrySectorLabelTranslation]) => ({
                                ...item,
                                label: this.utilsService.handleTranslation(labelTranslation),
                                industrySectorLabel:
                                    this.utilsService.handleTranslation(industrySectorLabelTranslation),
                            })),
                        );
                    });

                    // Use forkJoin to wait for all translation observables to complete
                    return forkJoin(translationObservables).pipe(
                        map((translatedActivities) => ({
                            quest,
                            uc,
                            lang,
                            translatedActivities,
                        })),
                    );
                }),
            )
            .subscribe(({ quest, uc, translatedActivities }) => {
                this.activities = translatedActivities;

                this.countryCitiesLUT = buildCountryCitiesLUT();
                this.translateService.get('countries').subscribe((countries) => {
                    this.countries = buildCountryOptions(countries, this.translateService.currentLang);
                });
                this.firstYears = Array.from({ length: 6 }, (_, index) => new Date().getFullYear() - index);
                if (uc && uc.organization && quest) {
                    const organizationFormGroup = this.organizationFormGroup;
                    organizationFormGroup.enable();
                    this.userClaim = uc;
                    this.questionnaire = quest;

                    //todo move this assignment to another callback or merge
                    [this.selectedIndustryActivity] = this.activities.filter(
                        (code) => code.industrySectorId === uc.organization?.industryId,
                    );
                    organizationFormGroup.controls['companyLogo'].setValue(uc.organization.avatar);
                    organizationFormGroup.controls['companyName'].setValue(uc.organization.name);
                    organizationFormGroup.controls['vatNumber'].setValue(uc.organization.tva);
                    organizationFormGroup.controls['country'].setValue(uc.organization.country);
                    // Reflect the loaded country's VAT format in the hint (kept pristine, so a
                    // legacy-invalid number is shown but not flagged until the user edits it).
                    this.vatPlaceholder =
                        countryData.find((c) => c.id === uc.organization?.country)?.placeholderVAT ?? 'BE0123.456.789';
                    if (organizationFormGroup.controls['country'].value === '') {
                        organizationFormGroup.controls['city'].disable();
                    } else {
                        this.filteredCities = this.countryCitiesLUT.get(uc.organization.country ?? '');
                        organizationFormGroup.controls['city'].setValue(
                            parseInt(uc.organization.cityCode ?? '0000000'),
                        );
                    }
                    organizationFormGroup.controls['naceCode'].setValue(uc.organization.nace);
                    organizationFormGroup.controls['industryCode'].setValue(uc.organization.industryId);
                    organizationFormGroup.controls['refYear'].setValue(quest.refYear);
                } else {
                    if (quest !== null) {
                        // Getting null is normal so we don't want to disable it...
                        this.organizationFormGroup.disable();
                    }
                }
            });

        this.authService.authenticatedUser$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (aU) => {
                const userFormGroup = this.userFormGroup;
                if (aU) {
                    const claims = aU as CognitoIdTokenClaims;
                    userFormGroup.controls['lastname'].setValue(claims.family_name ?? '');
                    userFormGroup.controls['firstname'].setValue(claims.given_name ?? '');
                    userFormGroup.controls['role'].setValue(claims['custom:role'] ?? '');
                    userFormGroup.controls['email'].setValue(claims.email ?? '');
                    const phoneNumber =
                        claims.phone_number && claims.phone_number !== '+3200000000000' ? claims.phone_number : null;
                    userFormGroup.controls['phoneNumber'].setValue(phoneNumber);
                } else {
                    userFormGroup.disable();
                }
            },
        });

        this.initializeFormValueSubscriptions();
    }

    private initializeFormValueSubscriptions(): void {
        this.organizationFormGroup.controls['naceCode'].valueChanges.subscribe((selectedNace) => {
            [this.selectedIndustryActivity] = this.activities.filter((code) => code.id === selectedNace);
            if (this.selectedIndustryActivity) {
                this.organizationFormGroup.controls['industryCode'].setValue(
                    this.selectedIndustryActivity.industrySectorId,
                );
            } else {
                this.organizationFormGroup.controls['industryCode'].reset();
            }
        });
    }

    onCountryChange(event: SelectChangeEvent) {
        const selectedCountryCode = event.value as string | undefined;
        if (selectedCountryCode && selectedCountryCode !== '') {
            const country: CountryType | undefined = countryData.find((c) => c.id === selectedCountryCode);
            const vatNumberControl = this.organizationFormGroup.controls['vatNumber'];
            // Keep the entered VAT and re-check it against the new country: mark it touched so a
            // number that is no longer valid surfaces its error (red outline + message) immediately.
            vatNumberControl.markAsTouched();
            vatNumberControl.updateValueAndValidity();
            this.vatPlaceholder = country?.placeholderVAT ?? 'BE0123.456.789';
        }
    }

    // Validate as soon as the user leaves the field (clicks away), not only after editing it.
    onVatBlur(): void {
        const vatNumberControl = this.organizationFormGroup.controls['vatNumber'];
        vatNumberControl.markAsTouched();
        vatNumberControl.updateValueAndValidity();
    }
    filterCities() {
        const country = this.organizationFormGroup.controls['country'].value as string | null;
        if (country) {
            this.filteredCities = this.countryCitiesLUT.get(country);
            this.organizationFormGroup.controls['city'].enable();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onSubmit() {
        // Only the sections this submit sends are checked: the password ones once the user starts
        // changing the password, the company one outside the admin form.
        const sections = [
            ...(this.isAdminForm ? [] : [this.organizationFormGroup]),
            this.userFormGroup,
            ...(this.securityFormGroup.pristine ? [] : [this.securityFormGroup]),
        ];
        const valid = sections.map((section) => revealFormErrors(section, this.host.nativeElement)).every(Boolean);
        if (!valid) {
            return;
        }

        const organizationFormGroup = this.organizationFormGroup;
        const userFormGroup = this.userFormGroup;
        const securityFormGroup = this.securityFormGroup;

        // here are initiated the collection and the subject to handle navigation
        // the signals are added in the array of signals to listen if the condition is met below

        const completionSignals: Subject<FormulaireCallCompletion>[] = [];
        let userInfoUpdateResult: Subject<FormulaireCallCompletion>;
        let organizationUpdateResult: Subject<FormulaireCallCompletion>;
        let questionnaireUpdateResult: Subject<FormulaireCallCompletion>;
        let passUpdateResult: Subject<FormulaireCallCompletion>;
        // this.organizationFormGroup.markAllAsTouched();

        this.loading = true;
        if (
            !this.isAdminForm &&
            this.userClaim.organization &&
            organizationFormGroup.valid &&
            (!organizationFormGroup.pristine || (organizationFormGroup.pristine && this.isOnboarding))
        ) {
            const org = this.userClaim.organization;
            const questionnaire = this.questionnaire;
            const orgControls = organizationFormGroup.controls;
            org.country = orgControls['country'].value as string | undefined;
            org.cityCode = orgControls['city'].value as string;
            org.name = orgControls['companyName'].value as string;
            org.tva = orgControls['vatNumber'].value as string | undefined;
            org.nace = orgControls['naceCode'].value as string | undefined;
            org.industryId = orgControls['industryCode'].value as string | undefined;
            org.avatar = orgControls['companyLogo'].value as string | undefined;
            questionnaire.refYear = orgControls['refYear'].value as number;
            org.isOnboarded = true;

            organizationUpdateResult = new Subject<FormulaireCallCompletion>();
            questionnaireUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(organizationUpdateResult, questionnaireUpdateResult);
            this.store.dispatch(new UserAction.UpdateOrganization(org, this.isOnboarding, organizationUpdateResult));
            if (org.uid) {
                this.store.dispatch(
                    new QuestionnaireAction.UpdatePropertyAction(org.uid, questionnaire, questionnaireUpdateResult),
                );
            }

            combineLatest([organizationUpdateResult, questionnaireUpdateResult])
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: (combo) => {
                        if (combo[0] === 'orgSuccess' && combo[1] === 'questSuccess') {
                            this.organizationFormGroup.markAsPristine();
                            this.toastService.showSuccess({
                                details: 'components.settingsForm.companySection.toasts.updateSuccess',
                            });
                        }
                        if (combo[0] === 'orgFail' || combo[1] === 'questFail') {
                            this.toastService.showError({
                                details: 'components.settingsForm.companySection.toasts.updateError',
                            });
                        }
                    },
                });
        }
        if (userFormGroup.valid && (!userFormGroup.pristine || (userFormGroup.pristine && this.isOnboarding))) {
            userInfoUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(userInfoUpdateResult);
            const userControls = userFormGroup.controls;
            this.authService
                .updateUserAttributes(
                    userControls['firstname'].value as string,
                    userControls['lastname'].value as string,
                    (userControls['phoneNumber'].value as string | null) ?? '+3200000000000',
                    userControls['role'].value as string,
                )
                .subscribe({
                    //todo : message in page or in toast
                    next: () => {
                        userInfoUpdateResult.next('userSuccess');
                        userInfoUpdateResult.complete();
                        userFormGroup.markAsPristine();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.userSection.toasts.updateSuccess',
                        });
                    },
                    error: () => {
                        userInfoUpdateResult.next('userFail');
                        userInfoUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.userSection.toasts.updateError',
                        });
                    },
                });
        }
        if (securityFormGroup.valid && !securityFormGroup.pristine) {
            passUpdateResult = new Subject<FormulaireCallCompletion>();
            completionSignals.push(passUpdateResult);
            const securityControls = securityFormGroup.controls;
            this.authService
                .changePassword(
                    securityControls['oldPassword'].value as string,
                    securityControls['newPassword'].value as string,
                )
                .subscribe({
                    //todo : message in page or in toast
                    next: () => {
                        securityFormGroup.reset();
                        // securityFormGroup.markAsPristine();
                        passUpdateResult.next('passSuccess');
                        passUpdateResult.complete();
                        this.toastService.showSuccess({
                            details: 'components.settingsForm.securitySection.toasts.updateSuccess',
                        });
                    },
                    error: () => {
                        passUpdateResult.next('passFail');
                        passUpdateResult.complete();
                        this.toastService.showError({
                            details: 'components.settingsForm.securitySection.toasts.updateError',
                        });
                    },
                });
        }

        /**
         * all the api calls emit a completion signal with a type allowing to know what has success/failed errors and toast can be called before but need to know if everything succeded before navigation.
         * it's also possible to handle success/error from this callback
         *  if (completionSignals.length !== 0) {
         *      this.loading = true;
         *  }
         */
        combineLatest(completionSignals)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (callbacks) => {
                    if (
                        this.isOnboarding &&
                        callbacks.includes('orgSuccess') &&
                        callbacks.includes('userSuccess') &&
                        this.userStatus === UserStatus.ONBOARDED
                    ) {
                        this.loading = false;
                        this.onboardedEvent.emit(true);
                    }

                    this.loading = false;
                },
            });
    }

    /** Whether there is anything to save: submitting an untouched form would send nothing. */
    public hasChanges(): boolean {
        const sections = this.isAdminForm
            ? [this.userFormGroup, this.securityFormGroup]
            : [this.organizationFormGroup, this.userFormGroup, this.securityFormGroup];
        return sections.some((section) => !section.pristine);
    }
}
